import buildTextInputDefinition, {
    TextInputDefinition
} from "../../../../../../../utils/forms/models/grid-fields/text-input-definition";
import buildNumberInputDefinition, {
    NumberInputDefinition
} from "../../../../../../../utils/forms/models/grid-fields/number-input-definition";
import { FormDefinition } from "../../../../../../../utils/forms/builders/build-form-grid-fields";
import buildButtonSelectorDefinition, {
    ButtonSelectorDefinition
} from "../../../../../../../utils/forms/models/grid-fields/button-selector-definition";
import buildSwitchDefinition, {
    SwitchDefinition
} from "../../../../../../../utils/forms/models/grid-fields/switch-definition";
import buildBulletsSelectorDefinition, {
    BulletsSelectorDefinition
} from "../../../../../../../utils/forms/models/grid-fields/bullets-selector-definition";

export const CARD_TITLE = "Global Jackpot";

export enum SCHEMA_NAMES {
    ID = "id",
    COIN = "coin",
    TERMINAL_GROUPS = "terminal_groups",
    GAME_GROUPS = "game_groups",
    GLOBAL_JACKPOT_SOUND = "global_jackpot_sound",
    GLOBAL_INCREMENT_SOUND = "global_increment_sound",
    GROUPS = "groups",
    INITIAL_VALUE = "initial_value",
    MAX_VALUE = "max_value",
    CONTRIBUTION = "contribution",
    MIN_BET = "min_bet",
    INCREMENT_SOUND_VALUE = "increment_sound_value"
}

const GLOBAL_JACKPOT_ID: TextInputDefinition = buildTextInputDefinition({
    name: "id",
    id: "global_jackpot_id",
    placeholder: "Insert ID",
    label: "ID",
    schemaName: SCHEMA_NAMES.ID,
    fieldOverrides: { disabled: true }
});

const COIN: (
    items: { label: string | number; value: string | number }[]
) => BulletsSelectorDefinition = items =>
    buildBulletsSelectorDefinition({
        name: "coin",
        id: "global_jackpot_coin",
        label: "Coin",
        items: items,
        completeList: items,
        schemaName: SCHEMA_NAMES.COIN,
        fieldOverrides: {
            formSpacing: false,
            multipleSelection: true,
            columnClass: "col-xs-12 col-sm-12 col-md-12 col-lg-12 col-xl-12"
        }
    });

const GAMES: (
    items: { label: string | number; value: string | number }[]
) => ButtonSelectorDefinition = items =>
    buildButtonSelectorDefinition({
        name: "game_groups",
        id: "global_jackpot_game_groups",
        label: "Games",
        items: items,
        schemaName: SCHEMA_NAMES.GAME_GROUPS
    });

const GLOBAL_JACKPOT_SOUND: SwitchDefinition = buildSwitchDefinition({
    name: "global_jackpot_sound",
    id: "global_jackpot_sound",
    label: "Global Jackpot Sound",
    schemaName: SCHEMA_NAMES.GLOBAL_JACKPOT_SOUND
});

const GLOBAL_INCREMENT_SOUND: SwitchDefinition = buildSwitchDefinition({
    name: "global_increment_sound",
    id: "global_inc_sound",
    label: "Globally Increment Sound",
    schemaName: SCHEMA_NAMES.GLOBAL_INCREMENT_SOUND
});

const INITIAL_VALUE: NumberInputDefinition = buildNumberInputDefinition({
    name: "initial_value",
    id: "init_value",
    placeholder: "Insert Initial Value",
    label: "Initial Value",
    schemaName: SCHEMA_NAMES.INITIAL_VALUE
});

const MAX_VALUE: NumberInputDefinition = buildNumberInputDefinition({
    name: "max_value",
    id: "max_value",
    placeholder: "Insert Max Value",
    label: "Maximum Value",
    schemaName: SCHEMA_NAMES.MAX_VALUE
});

const CONTRIBUTION: NumberInputDefinition = buildNumberInputDefinition({
    name: "contribution",
    id: "contrib",
    placeholder: "Insert Contribution",
    label: "Contribution",
    schemaName: SCHEMA_NAMES.CONTRIBUTION
});

const MIN_BET: NumberInputDefinition = buildNumberInputDefinition({
    name: "min_bet",
    id: "min_bet",
    placeholder: "Insert Minimum Bet",
    label: "Minimum Bet",
    schemaName: SCHEMA_NAMES.MIN_BET
});

const INCREMENT_SOUND_VALUE: NumberInputDefinition = buildNumberInputDefinition(
    {
        name: "increment_sound_value",
        id: "inc_sound_value",
        placeholder: "Insert Value to Play Increment Sound",
        label: "Value to Play Increment Sounds",
        schemaName: SCHEMA_NAMES.INCREMENT_SOUND_VALUE
    }
);

export function globalJackpotFormDefinition(
    gamesInstances: { label: string | number; value: string | number }[],
    coins: { label: string | number; value: string | number }[]
): FormDefinition {
    return {
        [SCHEMA_NAMES.ID]: {
            definition: {
                ...GLOBAL_JACKPOT_ID,
                fieldOverrides: {
                    formSpacing: false,
                    columnClass:
                        "col-xs-12 col-sm-12 col-md-6 col-lg-4 col-xl-4 hidden"
                }
            },
            order: 0
        },
        [SCHEMA_NAMES.COIN]: {
            definition: COIN(coins),
            order: 2
        },
        [SCHEMA_NAMES.GLOBAL_JACKPOT_SOUND]: {
            definition: {
                ...GLOBAL_JACKPOT_SOUND,
                fieldOverrides: {
                    formSpacing: false,
                    columnClass:
                        "col-xs-12 col-sm-12 col-md-6 col-lg-4 col-xl-4"
                }
            },
            order: 1
        },
        [SCHEMA_NAMES.GAME_GROUPS]: {
            definition: {
                ...GAMES(gamesInstances),
                fieldOverrides: {
                    formSpacing: false,
                    columnClass:
                        "col-xs-12 col-sm-12 col-md-12 col-lg-12 col-xl-12 button-selector"
                }
            },
            order: 3
        },
        [SCHEMA_NAMES.INITIAL_VALUE]: {
            definition: {
                ...INITIAL_VALUE,
                fieldOverrides: {
                    formSpacing: false,
                    columnClass:
                        "col-xs-12 col-sm-12 col-md-6 col-lg-4 col-xl-4"
                }
            },
            order: 4
        },
        [SCHEMA_NAMES.MAX_VALUE]: {
            definition: {
                ...MAX_VALUE,
                fieldOverrides: {
                    formSpacing: false,
                    columnClass:
                        "col-xs-12 col-sm-12 col-md-6 col-lg-4 col-xl-4"
                }
            },
            order: 5
        },
        [SCHEMA_NAMES.CONTRIBUTION]: {
            definition: {
                ...CONTRIBUTION,
                fieldOverrides: {
                    formSpacing: false,
                    columnClass:
                        "col-xs-12 col-sm-12 col-md-6 col-lg-4 col-xl-4 percentage"
                }
            },
            order: 6
        },
        [SCHEMA_NAMES.MIN_BET]: {
            definition: {
                ...MIN_BET,
                fieldOverrides: {
                    formSpacing: false,
                    columnClass:
                        "col-xs-12 col-sm-12 col-md-6 col-lg-4 col-xl-4"
                }
            },
            order: 7
        },
        [SCHEMA_NAMES.INCREMENT_SOUND_VALUE]: {
            definition: {
                ...INCREMENT_SOUND_VALUE,
                fieldOverrides: {
                    formSpacing: false,
                    columnClass:
                        "col-xs-12 col-sm-12 col-md-6 col-lg-4 col-xl-4"
                }
            },
            order: 8
        },
        [SCHEMA_NAMES.GLOBAL_INCREMENT_SOUND]: {
            definition: {
                ...GLOBAL_INCREMENT_SOUND,
                fieldOverrides: {
                    formSpacing: false,
                    columnClass:
                        "col-xs-12 col-sm-12 col-md-6 col-lg-4 col-xl-4"
                }
            },
            order: 9
        }
    };
}

export const GLOBAL_JACKPOT_FIELD_NAMES = {
    [SCHEMA_NAMES.COIN]: "Coin",
    [SCHEMA_NAMES.GAME_GROUPS]: "Games",
    [SCHEMA_NAMES.GLOBAL_JACKPOT_SOUND]: "Global Jackpot Sound",
    [SCHEMA_NAMES.GLOBAL_INCREMENT_SOUND]: "Globally Increment Sound",
    [SCHEMA_NAMES.INITIAL_VALUE]: "Initial Value",
    [SCHEMA_NAMES.MAX_VALUE]: "Maximum Value",
    [SCHEMA_NAMES.CONTRIBUTION]: "Contribution",
    [SCHEMA_NAMES.MIN_BET]: "Minimum Bet",
    [SCHEMA_NAMES.INCREMENT_SOUND_VALUE]: "Increment Sound Value"
};
